// Which alarms start an investigation, on one alert source (#847).
//
// Read when a person opens it, never with the list: the mode, the built-in
// noise list, the patterns in the order they are read, the account and region
// switches, and the alarms the source skipped lately with the rule that skipped
// each. The server validates a save and is the enforcement; a person who may
// not change the filter reads it and is told why when they try.
//
// The source's dry run (#849) is read with it: while one lasts, an alarm that would
// start an investigation is listed here and starts nothing, so the filter can be tuned
// against what would have happened before the source is switched on.

import { useState, type FormEvent } from "react";

import { severityLabel, utcTime } from "../api/automation";
import { messageOf } from "../api/http";
import {
  DEFAULT_DRY_RUN_DAYS,
  MAX_DRY_RUN_DAYS,
  MODES,
  NOISE,
  skipReasonLabel,
  type FilterRule,
  type DryRunRead,
  type FilterSettings,
  type SourceFilterRead,
} from "../api/sources";
import { Notice } from "./chrome";

const RULE_FIELDS: ReadonlyArray<[FilterRule["field"], string]> = [
  ["name", "Alarm name"],
  ["namespace", "Namespace"],
];

const RULE_ACTIONS: ReadonlyArray<[FilterRule["action"], string]> = [
  ["exclude", "Exclude"],
  ["include", "Include"],
];

export function SourceFilter({
  sourceId,
  readOnly,
  onRead,
  onSave,
  onSetDryRun,
}: {
  sourceId: string;
  readOnly: boolean;
  onRead: (id: string) => Promise<SourceFilterRead>;
  onSave: (id: string, settings: FilterSettings) => Promise<SourceFilterRead>;
  /** Start the source's dry run for some days, or end it with 0 (#849). */
  onSetDryRun?: (id: string, days: number) => Promise<SourceFilterRead>;
}) {
  const [read, setRead] = useState<SourceFilterRead | null>(null);
  const [draft, setDraft] = useState<FilterSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const answer = await onRead(sourceId);
      setRead(answer);
      setDraft(answer.settings);
    } catch (failure) {
      setError(messageOf(failure));
    }
  };

  const change = (next: FilterSettings) => {
    setSaved(false);
    setDraft(next);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (draft === null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const answer = await onSave(sourceId, {
        ...draft,
        rules: draft.rules.filter((rule) => rule.pattern.trim() !== ""),
      });
      setRead(answer);
      setDraft(answer.settings);
      setSaved(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details
      data-testid={`source-filter-${sourceId}`}
      onToggle={(event) => {
        if (event.currentTarget.open && read === null) void load();
      }}
    >
      <summary>Which alarms start an investigation</summary>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {read?.dry_run === undefined ? null : (
        <DryRun
          sourceId={sourceId}
          dryRun={read.dry_run}
          readOnly={readOnly || onSetDryRun === undefined}
          onSet={async (days) => {
            if (onSetDryRun === undefined) return;
            setRead(await onSetDryRun(sourceId, days));
          }}
        />
      )}
      {draft === null || read === null ? (
        error === null ? <p className="muted" role="status">Reading this source's filter…</p> : null
      ) : (
        <form className="stack" onSubmit={submit} aria-busy={busy}>
          <fieldset disabled={readOnly}>
            <legend className="field__label">Mode</legend>
            {MODES.map(([mode, label, hint]) => (
              <label key={mode} className="checkline">
                <input
                  type="radio"
                  name={`mode-${sourceId}`}
                  value={mode}
                  checked={draft.mode === mode}
                  onChange={() => change({ ...draft, mode })}
                />
                <span>
                  {label} <small className="field__hint">{hint}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset disabled={readOnly}>
            <legend className="field__label">Known noise, skipped while ticked</legend>
            {NOISE.map(([key, label, hint]) => (
              <label key={key} className="checkline">
                <input
                  type="checkbox"
                  checked={draft.noise[key]}
                  onChange={(event) =>
                    change({ ...draft, noise: { ...draft.noise, [key]: event.target.checked } })
                  }
                />
                <span>
                  {label} <small className="field__hint">{hint}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <Patterns rules={draft.rules} readOnly={readOnly} onChange={(rules) => change({ ...draft, rules })} />

          <Switches
            legend="Accounts"
            empty="No AWS account is connected to this workspace yet."
            options={read.accounts.map(({ account_id, name }) => [
              account_id,
              name === null ? account_id : `${name} (${account_id})`,
            ])}
            off={draft.accounts_off}
            readOnly={readOnly}
            onChange={(accounts_off) => change({ ...draft, accounts_off })}
          />
          <Switches
            legend="Regions"
            empty="No region is chosen on a connected account yet."
            options={read.regions.map((region) => [region, region])}
            off={draft.regions_off}
            readOnly={readOnly}
            onChange={(regions_off) => change({ ...draft, regions_off })}
          />

          {readOnly ? null : (
            <div className="actions">
              <button type="submit" className="btn btn--primary btn--small" disabled={busy}>
                {busy ? "Saving…" : "Save the filter"}
              </button>
              {saved ? <span role="status">Saved</span> : null}
            </div>
          )}

          <Skipped skipped={read.skipped} />
        </form>
      )}
    </details>
  );
}

/** The patterns, in the order they are read: the first that matches an alarm decides. */
function Patterns({
  rules,
  readOnly,
  onChange,
}: {
  rules: FilterRule[];
  readOnly: boolean;
  onChange: (rules: FilterRule[]) => void;
}) {
  const set = (index: number, rule: FilterRule) => onChange(rules.map((r, i) => (i === index ? rule : r)));
  const move = (index: number) => {
    const next = [...rules];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };
  return (
    <fieldset disabled={readOnly}>
      <legend className="field__label">Patterns</legend>
      <small className="field__hint">
        Read top to bottom; the first that matches an alarm decides. Use * to match any
        characters. Matching is case-sensitive.
      </small>
      {rules.length === 0 ? <p className="muted">No patterns.</p> : null}
      <ol className="stack stack--tight" aria-label="Patterns">
        {rules.map((rule, index) => (
          <li key={index} className="row row--wrap">
            <select
              className="input input--inline"
              aria-label={`Pattern ${index + 1} action`}
              value={rule.action}
              onChange={(event) => set(index, { ...rule, action: event.target.value as FilterRule["action"] })}
            >
              {RULE_ACTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="input input--inline"
              aria-label={`Pattern ${index + 1} matches`}
              value={rule.field}
              onChange={(event) => set(index, { ...rule, field: event.target.value as FilterRule["field"] })}
            >
              {RULE_FIELDS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <input
              className="input input--inline"
              aria-label={`Pattern ${index + 1}`}
              placeholder={rule.field === "name" ? "dev-*" : "AWS/Lambda"}
              maxLength={256}
              value={rule.pattern}
              onChange={(event) => set(index, { ...rule, pattern: event.target.value })}
            />
            {index === 0 ? null : (
              <button type="button" className="btn btn--small" onClick={() => move(index)}>
                Move up
              </button>
            )}
            <button
              type="button"
              className="btn btn--small btn--quiet"
              onClick={() => onChange(rules.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>
      <div className="actions">
        <button
          type="button"
          className="btn btn--small"
          onClick={() => onChange([...rules, { field: "name", action: "exclude", pattern: "" }])}
        >
          Add a pattern
        </button>
      </div>
    </fieldset>
  );
}

/** One switch per account or region: ticked starts investigations, unticked skips them. */
function Switches({
  legend,
  empty,
  options,
  off,
  readOnly,
  onChange,
}: {
  legend: string;
  empty: string;
  options: Array<[string, string]>;
  off: string[];
  readOnly: boolean;
  onChange: (off: string[]) => void;
}) {
  return (
    <fieldset disabled={readOnly}>
      <legend className="field__label">{legend}</legend>
      {options.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <div className="setup-region-grid">
          {options.map(([value, label]) => (
            <label key={value} className="checkline">
              <input
                type="checkbox"
                checked={!off.includes(value)}
                onChange={(event) =>
                  onChange(event.target.checked ? off.filter((v) => v !== value) : [...off, value])
                }
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function Skipped({ skipped }: { skipped: SourceFilterRead["skipped"] }) {
  return (
    <section aria-label="Recently skipped alarms" className="stack stack--tight">
      <h4>Recently skipped</h4>
      {skipped.length === 0 ? (
        <p className="muted">Nothing skipped in the last seven days.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Alarm</th>
                <th scope="col">Why</th>
                <th scope="col">Where</th>
              </tr>
            </thead>
            <tbody>
              {skipped.map((skip, index) => (
                <tr key={`${skip.received_at}-${index}`}>
                  <td>
                    <time dateTime={skip.received_at}>{skip.received_at.slice(0, 16).replace("T", " ")}</time>
                  </td>
                  <td className="break">{skip.alarm}</td>
                  <td>{skipReasonLabel(skip)}</td>
                  <td>{[skip.account_id, skip.region].filter(Boolean).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The dry run: its state, the way to start or end it, and every alarm it held. */
function DryRun({
  sourceId,
  dryRun,
  readOnly,
  onSet,
}: {
  sourceId: string;
  dryRun: DryRunRead;
  readOnly: boolean;
  onSet: (days: number) => Promise<void>;
}) {
  const [days, setDays] = useState(String(DEFAULT_DRY_RUN_DAYS));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = async (value: number) => {
    setBusy(true);
    setError(null);
    try {
      await onSet(value);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  const shown = dryRun.would_have_investigated;
  return (
    <section aria-label="Dry run" className="stack stack--tight">
      <h4>Dry run</h4>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {dryRun.until !== null ? (
        <p>
          In a dry run until {utcTime(dryRun.until)}. An alarm that would start an investigation is listed below and
          starts nothing. Ending the dry run starts nothing for what it listed.
        </p>
      ) : (
        <p className="muted">Not in a dry run: each alarm the filter lets through can start an investigation.</p>
      )}
      {readOnly ? null : dryRun.until !== null ? (
        <div className="actions">
          <button type="button" className="btn btn--small" disabled={busy} onClick={() => void set(0)}>
            {busy ? "Ending…" : "End the dry run now"}
          </button>
        </div>
      ) : (
        <div className="actions">
          <label className="field__label" htmlFor={`dry-run-days-${sourceId}`}>
            Days
          </label>
          <input
            id={`dry-run-days-${sourceId}`}
            className="input input--inline"
            type="number"
            min={1}
            max={MAX_DRY_RUN_DAYS}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
          <button type="button" className="btn btn--small" disabled={busy} onClick={() => void set(Number(days))}>
            {busy ? "Starting…" : "Start a dry run"}
          </button>
        </div>
      )}
      <section aria-label="Would have investigated" className="stack stack--tight">
        <h4>Would have investigated</h4>
        {shown.length === 0 ? (
          <p className="muted">No alarm yet.</p>
        ) : (
          <>
            {dryRun.total > shown.length ? (
              <small className="muted">
                The newest {shown.length} of {dryRun.total}.
              </small>
            ) : null}
            <div className="table-wrap" tabIndex={0}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">First</th>
                    <th scope="col">Alarm</th>
                    <th scope="col">Severity</th>
                    <th scope="col">Fired</th>
                    <th scope="col">Where</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row, index) => (
                    <tr key={`${row.held_at}-${index}`}>
                      <td>
                        <time dateTime={row.held_at}>{utcTime(row.held_at)}</time>
                      </td>
                      <td className="break">{row.alarm}</td>
                      <td>{severityLabel(row.severity)}</td>
                      <td>{row.occurrences === 1 ? "Once" : `${row.occurrences} times`}</td>
                      <td>{[row.account_id, row.region].filter(Boolean).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </section>
  );
}
